import React, { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';
import { Spinner } from './utilities';

export interface DeleteConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  /** Runs the deletion. The dialog stays open (with a spinner) until it settles. */
  onConfirm: () => void | Promise<void>;
  /** Name of the item being deleted; shown in the message when given */
  itemName?: string;
  /** Defaults to "Delete item?" */
  title?: string;
  /** Replaces the default message entirely */
  message?: string;
  /** Extra warning shown under the message (e.g. what else is removed) */
  details?: React.ReactNode;
  /** Defaults to "Delete" */
  confirmText?: string;
  /** Verb in the default message ("Are you sure you want to <verb> ..."). Defaults to "delete" */
  verb?: string;
}

/**
 * The one confirmation used before every delete action in the app:
 * title "Delete item?", Cancel + red Delete. Deletion runs only from the
 * Delete button.
 */
export const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  itemName,
  title = 'Delete item?',
  message,
  details,
  confirmText = 'Delete',
  verb = 'delete',
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Each opening starts clean
  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const handleConfirm = async (e: React.MouseEvent) => {
    // Radix closes the dialog on Action click; keep it open until the delete settles
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      // Stay open so the user sees the delete failed; no unhandled rejection
      setError(err instanceof Error && err.message ? err.message : 'The delete failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && !loading && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>
            {message ?? (
              itemName ? (
                <>
                  Are you sure you want to {verb}
                  {/* Name on its own line so long names wrap whole, not mid-word */}
                  <strong className="block mt-1 text-foreground break-words">"{itemName}"?</strong>
                </>
              ) : (
                `Are you sure you want to ${verb} this item?`
              )
            )}
          </AlertDialogDescription>
          {details && <div className="text-sm text-muted-foreground">{details}</div>}
          {error && (
            <div role="alert" className="text-sm text-destructive break-words">
              {error}
            </div>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={loading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {loading && <Spinner size="sm" className="mr-2" />}
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
